import { VFC } from 'react';
import { Table, TablePaginationConfig } from 'antd';
import { FilterConfirmProps, FilterValue, SorterResult } from 'antd/lib/table/interface';
import { useIntl } from 'react-intl';
import { SerializedMeter } from '@enmon/commons/lib/enmon';
import { SerializedInventoryLocationBuilding } from '@enmon/commons/lib/tools';
import { Commodity, isCommodity } from '@enmon/commons/lib/shared';
import { useSessionStore } from '../../../store/session';
import { useMetersTable } from '../hooks/useMetersTable';
import { getLocationBuildingFilters, getCommodityFilters } from '../utils/getFilters';
import { useStore } from '../useStore';
import { useStore as useInventorizeStore } from '../../inventorize_meter/useStore';
import { MetersTableMeter } from '../queries/getMetersTableData';
import { DEFAULT_FILTER, MeterListFilter } from '../const';
import { getBuildingFloorOptions } from '../../../consts/common';
import { getColumns } from '../utils/getColumns';

export const MeterListTable: VFC = () => {
  const intl = useIntl();
  const { locale } = useSessionStore();
  const { location } = useInventorizeStore();
  const { filter, pagination, updateFilter, updatePagination } = useStore();

  const { meters, isLoading } = useMetersTable();

  const handleSearch = (
    selectedKeys: React.Key[],
    confirm: (param?: FilterConfirmProps) => void,
    dataIndex: string,
  ) => {
    confirm();
    updateFilter({ [dataIndex]: selectedKeys.length ? selectedKeys : [''] });
  };

  const handleReset = (clearFilters: () => void, item: keyof MeterListFilter) => {
    clearFilters();
    updateFilter({ [item]: DEFAULT_FILTER[item] });
  };

  const handleTableChange = (
    changedPagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<MetersTableMeter> | SorterResult<MetersTableMeter>[],
  ) => {
    const { field, order } = Array.isArray(sorter) ? sorter[0] ?? {} : sorter;
    const updatedSortVariable = field ? (Array.isArray(field) ? field : [field]).join('.') : DEFAULT_FILTER.sortField;
    updateFilter({
      sortOrder: order ?? DEFAULT_FILTER.sortOrder,
      sortField: !order ? DEFAULT_FILTER.sortField : updatedSortVariable,
      locationBuildings: filters.locationBuildings?.filter(
        (id): id is SerializedInventoryLocationBuilding['id'] => typeof id === 'number',
      ),
      buildingFloors: filters.buildingFloors?.filter(
        (floor): floor is NonNullable<SerializedMeter['buildingFloor']> => typeof floor === 'number',
      ),
      commodities: filters.commodities?.filter(
        (type): type is Commodity => typeof type === 'string' && isCommodity(type),
      ),
    });
    updatePagination({
      current: changedPagination.current ?? 1,
      pageSize: changedPagination.pageSize,
    });
  };

  return (
    <Table
      columns={getColumns({
        activeFilter: filter,
        filterItems: {
          commodities: getCommodityFilters(intl),
          locationBuildings: getLocationBuildingFilters(location?.inventory_location_buildings ?? []),
          buildingFloors: getBuildingFloorOptions(locale, intl).map(({ label, value }) => ({ text: label, value })),
        },
        intl,
        onSearch: handleSearch,
        onReset: handleReset,
      })}
      dataSource={meters.slice(0, pagination.pageSize)}
      loading={isLoading}
      pagination={pagination}
      rowKey={item => item._id}
      scroll={{ x: 'max-content' }}
      onChange={handleTableChange}
    />
  );
};
